import { useEffect } from 'react';
import { refreshSessionCookie } from '@/store/authStore';

// Refresh well within the 12h access-token window so the session never lapses
// mid-use. Throttled so tab-focus bursts don't spam the endpoint.
const REFRESH_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
const MIN_GAP_MS = 5 * 60 * 1000; // at most once per 5 minutes
let lastRefresh = 0;

async function refreshSession(): Promise<void> {
  const now = Date.now();
  if (now - lastRefresh < MIN_GAP_MS) return;
  lastRefresh = now;
  // The one shared refresh (store/authStore.ts refreshSessionCookie): the refresh token rotates,
  // so this timer racing a hydrate's or the api client's refresh would have one of them answered
  // 401 and sign the tab out. Best-effort — it never throws, and a genuine expiry is still handled
  // by the api client's reactive 401 → refresh → /login fallback.
  await refreshSessionCookie();
}

/**
 * PROACTIVELY keep the authenticated session alive so long edit sessions and
 * stepping-away don't end in a surprise logout. Refreshes on an interval and when
 * the tab regains focus (throttled). The api client's reactive 401→refresh remains
 * the fallback for an actual lapse.
 */
export function useSessionKeepAlive(): void {
  useEffect(() => {
    const id = window.setInterval(() => { void refreshSession(); }, REFRESH_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshSession();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
}
